/**
 * 폭 맞춤 표 — 컨테이너 폭에 들어가는 열까지만 그리고 나머지는 생략한다(가로 스크롤 없음).
 *
 * Agent Chat 패널은 좌측 핸들로 너비를 조절할 수 있어 가로 스크롤은 조작 수단이 겹치는
 * 잘못된 UI 다 — 대신 폭을 넓히면 보이는 열이 늘어난다. 생략 시 표 아래 캡션 + 툴팁으로
 * 생략된 열 이름을 알린다.
 *
 * 측정·패킹·가용폭 관측·셀 스타일은 fitColumns.js 가 들고 있다.
 * 계층 표(TreeTable)가 같은 말풍선 안에 나란히 서므로 그 기계를 함께 쓴다.
 * 이 파일에 남은 것은 마크다운 표의 자연폭 산출과 행 렌더뿐이다.
 *
 * 중립 leaf — pages/** 를 import 하지 않는다(aiMarkdown.js 와 같은 규약).
 */
import { useRef } from "react";
import { Box } from "@mui/material";

import { C } from "@plannel/components/ai/aiTokens";
import {
  CELL_PAD, CELL_SX, MEASURE_ROWS, OmittedColsNote,
  columnCaps, headSx, measureText, useCellFont, useColumnFit,
} from "@plannel/components/ai/fitColumns";

/**
 * @param {{label: string, num?: boolean}[]} header
 * @param {{text: string, num?: boolean, bold?: boolean, html?: string}[][]} body
 *   html 이 있으면 셀을 innerHTML 로 그린다(마크다운 인라인 서식). 측정은 text 로 한다.
 * @param {number} maxHeight 0 이면 스크롤 박스 없이 본문 흐름에 그대로(마크다운 표),
 *   양수면 세로 스크롤 박스 + 헤더 고정(서버 ```table 펜스)
 * @param {boolean} bordered 표 둘레 테두리 박스(서버 ```table 펜스만)
 */
export default function FitTable({ header = [], body = [], maxHeight = 0, bordered = false }) {
  const { lang, fontEpoch, bodyPx, bodyFont, boldFont } = useCellFont();

  // 자연폭 단조 최대 누적 — 스트리밍은 행이 뒤에 붙는 형태라 델타마다 전수 재측정하지 않고
  // 새 행만 잰다. 한 번 잰 폭을 줄이지 않으므로 행 도착 중 열 폭이 떨리지 않고,
  // 최대 누적은 멱등이라 StrictMode 이중 호출에도 안전하다.
  // 키(헤더 라벨 + 언어 + 폰트 세대)가 바뀌면 다른 표 — 초기화한다(피벗 토글이 여기 해당).
  // 본문 px 는 키에도 들어간다 — Big/Small 토글 시 열 폭을 다시 재야 하기 때문(안 넣으면 옛 폭 유지).
  const natRef = useRef({ key: "", w: [], rows: 0 });
  const key = header.map((h) => h.label).join("\u0001") + "\u0002" + lang + "\u0002" + fontEpoch + "|" + bodyPx;
  if (natRef.current.key !== key) natRef.current = { key, w: [], rows: 0 };
  const nat = natRef.current;
  if (nat.w.length !== header.length) {
    nat.w = header.map((h) => measureText(h.label, boldFont) + CELL_PAD);
    nat.rows = 0;
  }
  const upto = Math.min(body.length, MEASURE_ROWS);
  for (let r = nat.rows; r < upto; r += 1) {
    const row = body[r];
    for (let c = 0; c < header.length; c += 1) {
      const cell = row[c];
      if (!cell) continue;
      const wpx = measureText(cell.text, cell.bold ? boldFont : bodyFont) + CELL_PAD;
      if (wpx > nat.w[c]) nat.w[c] = wpx;
    }
  }
  // 스트리밍 중 마지막 행은 글자가 이어 붙으며 자라므로 다음 렌더에서 다시 잰다
  nat.rows = Math.max(0, upto - 1);
  // 패킹이 ref 로 읽을 수 있게 열별 상한을 실어 둔다(의존성 배열 회피)
  nat.caps = columnCaps(header.map((h) => !!h.num));

  const { scrollRef, colRefs, widthsRef, visK } = useColumnFit(natRef, header.length);

  if (header.length === 0) return null;

  const k = Math.min(visK, header.length);
  const visHeader = header.slice(0, k);
  const omittedCount = header.length - k;
  const headCellSx = headSx(maxHeight);

  return (
    <Box sx={{ width: "100%", my: 0.8 }}>
      <Box ref={scrollRef}
        sx={{
          ...(maxHeight
            ? { maxHeight, overflowY: "auto", scrollbarGutter: "stable" }
            : null),
          overflowX: "hidden",
          ...(bordered ? { border: `0.5px solid ${C.border}`, borderRadius: "4px" } : null),
        }}>
        <Box component="table"
          sx={{ tableLayout: "fixed", width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
          <colgroup>
            {visHeader.map((h, i) => (
              <col key={i}
                ref={(el) => { colRefs.current[i] = el; }}
                style={{ width: widthsRef.current[i] ? `${widthsRef.current[i]}px` : undefined }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {visHeader.map((h, i) => (
                <Box component="th" key={i} sx={{ ...headCellSx, textAlign: h.num ? "right" : "left" }}>
                  {h.label}
                </Box>
              ))}
            </tr>
          </thead>
          <tbody>
            {body.map((r, ri) => (
              <tr key={ri}>
                {r.slice(0, k).map((c, ci) => (
                  c && c.html !== undefined
                    ? <Box component="td" key={ci}
                        sx={{ ...CELL_SX, textAlign: c.num ? "right" : "left" }}
                        dangerouslySetInnerHTML={{ __html: c.html }} />
                    : <Box component="td" key={ci}
                        sx={{ ...CELL_SX, textAlign: c?.num ? "right" : "left" }}>
                        {c ? c.text : ""}
                      </Box>
                ))}
              </tr>
            ))}
          </tbody>
        </Box>
      </Box>
      <OmittedColsNote n={omittedCount} />
    </Box>
  );
}
